import moment from 'moment';
import { MomentHelper } from 'src/app/core/models/moment-helper';
import { DateRange } from 'src/app/core/models/date-range';
import { DataUtils } from '@/core/utils/data-utils';

export class DateRangeHelper {
    public static getToday(): DateRange {
        return [MomentHelper.startOfDay(), MomentHelper.endOfDay()];
    }

    public static getYesterday(): DateRange {
        return [
            MomentHelper.startOfDay(moment().locale('de').subtract(1, 'day').toDate()),
            MomentHelper.endOfDay(moment().locale('de').subtract(1, 'day').toDate()),
        ];
    }

    public static getThisWeek(firstDayOfWeek: 0 | 1 = 0): DateRange {
        return DateRangeHelper.getWeek(0, firstDayOfWeek);
    }

    public static getLastWeek(firstDayOfWeek: 0 | 1 = 0): DateRange {
        return DateRangeHelper.getWeek(1, firstDayOfWeek);
    }

    private static getWeek(beforeNow: number, firstDayOfWeek: 0 | 1 = 0): DateRange {
        if (firstDayOfWeek === 1) {
            // Note: isoWeek starts with Monday
            const lastWeekNumber = moment().isoWeek() - beforeNow;
            const start = moment().isoWeek(lastWeekNumber).startOf('isoWeek').toDate();
            const end = moment().isoWeek(lastWeekNumber).endOf('isoWeek').toDate();
            return [start, end];
        }

        // Note: week starts with Sunday
        const week = moment().week() - beforeNow;
        const start = moment().week(week).startOf('week').toDate();
        const end = moment().week(week).endOf('week').toDate();
        return [start, end];
    }

    static getThisMonth(): DateRange {
        const month = moment().locale('de').month();
        const start = moment().locale('de').month(month).startOf('month').toDate();
        const end = moment().locale('de').month(month).endOf('month').toDate();
        return [start, end];
    }

    public static getLastMonth(): DateRange {
        const month = moment().locale('de').month() - 1;
        const start = moment().locale('de').month(month).startOf('month').toDate();
        const end = moment().locale('de').month(month).endOf('month').toDate();
        return [start, end];
    }

    public static getLast30Days(): DateRange {
        const start = MomentHelper.startOfDay(moment().locale('de').subtract(30, 'day').toDate());
        const end = MomentHelper.startOfDay();
        return [start, end];
    }

    public static getLast12Months(): DateRange {
        const start = MomentHelper.startOfDay(moment().locale('de').subtract(12, 'month').toDate());
        const end = MomentHelper.startOfDay();
        return [start, end];
    }

    public static getThisQuarter(): DateRange {
        const quarter = moment().locale('de').quarter();
        const start = moment().locale('de').quarter(quarter).startOf('quarter').toDate();
        const end = moment().locale('de').quarter(quarter).endOf('quarter').toDate();
        return [start, end];
    }

    public static getLastQuarter(): DateRange {
        const quarter = moment().locale('de').quarter() - 1;
        const start = moment().locale('de').quarter(quarter).startOf('quarter').toDate();
        const end = moment().locale('de').quarter(quarter).endOf('quarter').toDate();
        return [start, end];
    }

    public static getThisYear(): DateRange {
        const year = moment().locale('de').year();
        const start = moment().locale('de').year(year).startOf('year').toDate();
        const end = moment().locale('de').year(year).endOf('year').toDate();
        return [start, end];
    }

    public static getLastYear(): DateRange {
        const year = moment().locale('de').year() - 1;
        const start = moment().locale('de').year(year).startOf('year').toDate();
        const end = moment().locale('de').year(year).endOf('year').toDate();
        return [start, end];
    }

    public static default(): DateRange {
        return DateRangeHelper.getLast30Days();
    }

    public static getStart(dr: DateRange): Date | undefined {
        return dr[0];
    }

    public static getStartNotNull(dr: DateRange): Date {
        return dr[0] ? dr[0] : DateRangeHelper.default()[0];
    }

    /**
     * @param dr The DateRange object containing start and end
     * @returns The end of the date range, if that is null, it returns the start date
     */
    public static getEndNotNull(dr: DateRange): Date {
        return dr[1] ? dr[1] : DateRangeHelper.getStartNotNull(dr);
    }

    /**
     * @param dr DateRange object containing the start datetime
     * @returns The start datetime of the date range altered to the start of day (00:00 / 12:00 am)
     */
    public static getStartDate(dr: DateRange): Date {
        return MomentHelper.startOfDay(dr[0]);
    }

    /**
     * Gets the end of the date range, if not set the start day, but with the end of the day.
     * @param dr DateRange object containing the start datetime
     * @returns the end datetime of the date range altered to the end of day (23:59:59.999)
     */
    public static getEndDate(dr: DateRange): Date {
        return MomentHelper.endOfDay(DateRangeHelper.getEndNotNull(dr));
    }

    /**
     * Checks whether the given date range is valid.
     * A valid date range is an array with two elements, both of which are dates.
     * The first date must be before the second date.
     * @param dateRange The date range to be checked.
     * @returns true if the date range is valid, false otherwise.
     */
    public static isValid(dateRange: DateRange | undefined | null): boolean {
        if (!Array.isArray(dateRange) || dateRange.length !== 2) return false;

        const [from, to] = dateRange;

        if (!DataUtils.isValidDate(from) || !DataUtils.isValidDate(to)) return false;
        if (from.getTime() > to.getTime()) return false;

        return true;
    }

    public static getDurationDays(dr: DateRange): number {
        return moment(dr[1]).diff(moment(dr[0]), 'days');
    }
}
